import { useCallback, useMemo, useRef } from 'react';

import CustomButton from '../../common/CustomButton/CustomButton';
import CustomLink from '../../common/CustomLink/CustomLink';
import { Icon } from '../../common/Icon/Icon';
import Markdown from '../../common/Markdown/Markdown';
import NoTagRenderer from '../../common/NoTagRenderer/NoTagRenderer';
import { TBackground, addBackground, slugify } from '../../core/helpers';
import useFeedback from '../../core/hooks/useFeedback';
import useModal from '../../core/hooks/useModal';
import { ILink } from '../../core/models';
import Chat, { IVertexAI } from './Chat/Chat';
import './Vertex.css';

interface IProps {
  title: string;
  desc?: string;
  cta?: ILink;
  vertexai?: IVertexAI;
  background?: TBackground;
}

function Vertex({ title, desc, cta, vertexai, background }: IProps) {
  const btnRef = useRef(null);
  const {
    closeModal,
    startTimer,
    feedbackCookie,
    Modal: FeedbackModal,
  } = useFeedback({ ai: vertexai, btnRef });
  const closeCallback = useMemo(
    () => ({
      closeCallback: closeModal,
    }),
    [closeModal],
  );
  const { modal, open, close } = useModal(closeCallback);
  const openModal = useCallback(() => {
    if (!btnRef.current) return;
    open(btnRef.current);
    if (!feedbackCookie) startTimer();
  }, [feedbackCookie, open, startTimer]);

  return (
    <section
      className={`vertex${background ? ' section-bg' : ' section'}`}
      {...addBackground({ img: undefined, gradient: background })}
    >
      <div className="wrap">
        <div className="vertex__container gap-lg">
          <div className="vertex__left">
            <h2 className="vertex__title h2">
              <Markdown
                components={{
                  p: NoTagRenderer,
                }}
              >
                {title}
              </Markdown>
            </h2>
          </div>
          <div className="vertex__right gap-md">
            {!desc ? null : <Markdown className="vertex__desc markdown h3">{desc}</Markdown>}
            {!cta && !vertexai ? null : (
              <div className="vertex__btn-container">
                {!cta ? null : <CustomLink className="vertex__cta" link={cta} />}
                {!vertexai ? null : (
                  <CustomButton
                    className="btn"
                    onClick={openModal}
                    type="button"
                    ref={btnRef}
                    id={vertexai?.btnId}
                  >
                    {vertexai.btnLabel || 'Open'}
                  </CustomButton>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {!vertexai
        ? null
        : modal({
            side: 'right',
            opaque: true,
            keepMounted: true,
            children: (
              <>
                <CustomButton
                  className="modal__close"
                  onClick={close}
                  type="button"
                  id={`${slugify(title)}-modal-close`}
                >
                  <Icon.Plus className="icon-large" />
                  <span className="sr-only">Close modal</span>
                </CustomButton>
                {!vertexai ? null : <Chat ai={vertexai} />}
              </>
            ),
          })}
      <FeedbackModal />
    </section>
  );
}

export default Vertex;
